import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Tag, Gift, Edit, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const CouponsTab = ({
  coupons,
  couponsLoading,
  setEditingCoupon,
  setCouponForm,
  setShowCouponModal,
  openEditCouponModal,
  handleDeleteCoupon
}) => {
  
  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setCouponForm({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_order_amount: 0,
      max_uses: null,
      max_uses_per_customer: 1,
      valid_from: '',
      valid_until: '',
      is_active: true
    });
    setShowCouponModal(true);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Coupons & Discounts</h1>
          <p className="text-[var(--text-muted)] mt-1">Create and manage promotional codes for customers</p>
        </div>
        <Button 
          onClick={handleAddCoupon} 
          className="btn-primary" 
          data-testid="add-coupon-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-[#F4C2C2]/10 border border-[#F4C2C2]/30 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Gift className="w-5 h-5 text-[#F4C2C2] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-800">Automatic 10% First-Time Customer Discount</h3>
            <p className="text-sm text-purple-700 mt-1">
              All first-time customers automatically receive a 10% discount on their first booking. 
              Coupons here are additional discounts that can be stacked.
            </p>
          </div>
        </div>
      </div>

      {couponsLoading ? (
        <div className="card p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading coupons...</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="card p-12 text-center">
          <Tag className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Coupons Yet</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            Create your first coupon code to offer discounts to customers.
          </p>
          <Button 
            onClick={() => setShowCouponModal(true)} 
            className="btn-primary"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create First Coupon
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coupons.map(coupon => (
            <div 
              key={coupon.id} 
              className={`card p-6 ${!coupon.is_active ? 'opacity-60' : ''}`}
              data-testid={`coupon-card-${coupon.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-[#F4C2C2]" />
                  <span className={`badge-${coupon.is_active ? 'success' : 'secondary'}`}>
                    {coupon.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => openEditCouponModal(coupon)}
                    className="w-8 h-8"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => handleDeleteCoupon(coupon.id)}
                    className="w-8 h-8 text-[var(--error)] hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-[#F4C2C2]/15 rounded-lg px-4 py-3 mb-4 text-center">
                <span className="text-2xl font-bold text-purple-700 font-mono tracking-wider">
                  {coupon.code}
                </span>
              </div>

              <p className="text-sm text-[var(--text-secondary)] mb-4">
                {coupon.description}
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Discount:</span>
                  <span className="font-semibold text-[#F4C2C2]">
                    {coupon.discount_type === 'percentage' 
                      ? `${coupon.discount_value}% off` 
                      : `$${coupon.discount_value} off`
                    }
                  </span>
                </div>
                {coupon.min_order_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Min. Order:</span>
                    <span>${coupon.min_order_amount}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Used:</span>
                  <span>
                    {coupon.times_used} / {coupon.max_uses || '∞'}
                  </span>
                </div>
                {coupon.valid_until && (
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Expires:</span>
                    <span>{new Date(coupon.valid_until).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default CouponsTab;
